/**
 * What changed between two versions of a payload.
 *
 * **This must agree with `packages/core/diff.py`.** The server computes the diff the
 * reviewers see; this one computes the diff the applicant sees before raising. If they
 * disagree, someone confirms one set of changes and the chain reviews another — which is
 * worse than having no preview at all, because the preview is what they trusted.
 *
 * The rules are therefore copied deliberately rather than approximated, and
 * `tests/diff.test.ts` pins the same cases the Python suite pins:
 *
 *   · numbers compare as numbers, so "1000.00" and 1000 are not a change;
 *   · surrounding whitespace is not an amendment;
 *   · a boolean is never equal to a number, because "locked: true" and "locked: 1" are
 *     different claims in a governance record;
 *   · null and 0 are different, and so are null and "" — "not stated" and "zero" are
 *     different answers, especially about money.
 *
 * Two implementations of one rule is a defect waiting to happen. It is accepted here because
 * the alternative — a round trip to preview a diff the user has not yet asked for — makes
 * the edit flow feel broken on a slow connection, and this is a small, fully-tested rule.
 */

export type Change = {
  field: string
  kind: 'added' | 'removed' | 'changed'
  before: unknown
  after: unknown
}

export function diff(
  before: Record<string, unknown> | null | undefined,
  after: Record<string, unknown> | null | undefined,
): Change[] {
  const old = before ?? {}
  const next = after ?? {}
  const fields = [...new Set([...Object.keys(old), ...Object.keys(next)])].sort()

  const changes: Change[] = []
  for (const field of fields) {
    const inOld = field in old
    const inNew = field in next

    if (inNew && !inOld) {
      changes.push({ field, kind: 'added', before: null, after: next[field] })
    } else if (inOld && !inNew) {
      changes.push({ field, kind: 'removed', before: old[field], after: null })
    } else if (!equivalent(old[field], next[field])) {
      changes.push({ field, kind: 'changed', before: old[field], after: next[field] })
    }
  }
  return changes
}

export function equivalent(left: unknown, right: unknown): boolean {
  // Booleans first, and deliberately: in JavaScript `true == 1`, so an equality shortcut
  // here would report a flag and a number as the same value.
  if (typeof left === 'boolean' || typeof right === 'boolean') return left === right

  // null and undefined both mean "nothing"; either is different from 0 or "".
  const leftEmpty = left === null || left === undefined
  const rightEmpty = right === null || right === undefined
  if (leftEmpty || rightEmpty) return leftEmpty && rightEmpty

  if (left === right) return true

  const leftNumber = asNumber(left)
  const rightNumber = asNumber(right)
  if (leftNumber !== null && rightNumber !== null) return leftNumber === rightNumber

  if (typeof left === 'string' && typeof right === 'string') {
    return left.trim() === right.trim()
  }

  return false
}

function asNumber(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value === 'string') {
    const text = value.trim()
    if (text === '') return null
    const parsed = Number(text)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}
